import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatINR } from '../utils/priceFormat';

const PH = 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=100&q=60';
const resImg = ph => !ph ? PH : ph.startsWith('http') ? ph : `https://shop.venkatagirihandlooms.com/${ph.replace(/^\/+/,'')}`;

const STATUS_META = {
  confirmed:  { color:'#1565C0', bg:'#e3f2fd', icon:'✅', label:'Order Confirmed'  },
  processing: { color:'#E65100', bg:'#fff3e0', icon:'⚙️', label:'Processing'       },
  shipped:    { color:'#6A1B9A', bg:'#f3e5f5', icon:'🚚', label:'Shipped'          },
  out_for_delivery: { color:'#00695C', bg:'#e0f2f1', icon:'📦', label:'Out for Delivery' },
  delivered:  { color:'#2E7D32', bg:'#e8f5e9', icon:'🎉', label:'Delivered'        },
  cancelled:  { color:'#c00',    bg:'#ffebee', icon:'❌', label:'Cancelled'        },
};

// Order timeline steps
const TIMELINE_STEPS = [
  { key:'confirmed',        icon:'📋', label:'Order Placed',      detail:'Your order has been confirmed' },
  { key:'processing',       icon:'⚙️', label:'Being Prepared',    detail:'Our team is processing your order' },
  { key:'shipped',          icon:'🚚', label:'Shipped',           detail:'Your saree is on its way!' },
  { key:'out_for_delivery', icon:'📦', label:'Out for Delivery',  detail:'Almost there! Out for delivery' },
  { key:'delivered',        icon:'🎉', label:'Delivered',         detail:'Enjoy your beautiful saree!' },
];

const STATUS_ORDER = ['confirmed','processing','shipped','out_for_delivery','delivered'];

export default function OrdersPage() {
  const { user }   = useAuth();
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [orders,   setOrders]   = useState([]);
  const [detail,   setDetail]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [detLoading, setDetLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get('/orders').then(r => setOrders(r.data || [])).catch(()=>{}).finally(()=>setLoading(false));
  }, [user]);

  useEffect(() => {
    if (id && user) {
      setDetLoading(true);
      api.get(`/orders/${id}`).then(r => setDetail(r.data)).catch(()=>{}).finally(()=>setDetLoading(false));
    } else {
      setDetail(null);
    }
  }, [id, user]);

  if (!user) return (
    <div className="page" style={{textAlign:'center',paddingTop:120}}>
      <div style={{fontSize:56}}>📦</div>
      <h2 style={{fontFamily:"'Playfair Display',serif",color:'#7a1f1f',fontStyle:'italic',marginTop:12,marginBottom:16}}>My Orders</h2>
      <Link to="/login" className="btn btn-primary">Login to view orders</Link>
    </div>
  );

  if (loading) return <div className="page"><div className="spinner"/></div>;

  // ── ORDER DETAIL VIEW ─────────────────────────────────────────
  if (id) {
    if (detLoading) return <div className="page"><div className="spinner"/></div>;
    if (!detail) return (
      <div className="page" style={{textAlign:'center',paddingTop:120}}>
        <p style={{color:'#8a7968'}}>Order not found.</p>
        <Link to="/orders" className="btn btn-outline" style={{marginTop:16}}>← All Orders</Link>
      </div>
    );

    const statusIdx = STATUS_ORDER.indexOf(detail.status);
    const meta      = STATUS_META[detail.status] || STATUS_META.confirmed;

    return (
      <div className="page">
        <div className="container" style={{padding:'40px 24px 72px',maxWidth:960}}>

          {/* Back */}
          <button onClick={()=>navigate('/orders')} style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#8a7968',background:'none',border:'none',cursor:'pointer',fontFamily:"'Jost',sans-serif",letterSpacing:1,textTransform:'uppercase',marginBottom:28,padding:0}}>
            ← All Orders
          </button>

          {/* Header */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:16,marginBottom:36}}>
            <div>
              <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:2,textTransform:'uppercase',color:'#8a7968',marginBottom:6}}>Order ID</div>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:'clamp(1.3rem,2.5vw,1.8rem)',color:'#1a0f0f',fontStyle:'italic'}}>{detail.id}</h1>
              <div style={{fontSize:12,color:'#8a7968',marginTop:4,fontFamily:"'Jost',sans-serif"}}>
                Placed on {new Date(detail.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}
                {detail.payment_method && ` · ${detail.payment_method}`}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',fontSize:12,fontWeight:700,background:meta.bg,color:meta.color,border:`1px solid ${meta.color}33`,fontFamily:"'Jost',sans-serif",letterSpacing:1,textTransform:'uppercase'}}>
                {meta.icon} {meta.label}
              </span>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:32,alignItems:'start'}}>
            <div>
              {/* ── ORDER TIMELINE ── */}
              {detail.status !== 'cancelled' && (
                <div style={{background:'#fff',border:'1px solid var(--border)',padding:24,marginBottom:24}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:3,textTransform:'uppercase',color:'#8a7968',marginBottom:20}}>Delivery Status</div>
                  <div className="order-timeline">
                    {TIMELINE_STEPS.map((step, i) => { /* key={step.key} set on returned div */
                      const isDone   = STATUS_ORDER.indexOf(step.key) <= statusIdx && statusIdx >= 0;
                      const isActive = step.key === detail.status;
                      const isLast   = i === TIMELINE_STEPS.length - 1;
                      return (
                        <div key={step.key} className={`timeline-step ${isDone?'done':''}`}>
                          {!isLast && <div className={`timeline-line ${isDone?'done':''}`}/>}
                          <div className={`timeline-dot ${isActive?'active':isDone?'done':'pending'}`}>
                            {isDone ? (isActive ? step.icon : '✓') : step.icon}
                          </div>
                          <div className="timeline-content">
                            <div className="timeline-label" style={{color:isActive?'#7a1f1f':isDone?'#2e7d32':'#bbb'}}>{step.label}</div>
                            <div className="timeline-detail">{isActive ? step.detail : isDone ? 'Completed' : 'Pending'}</div>
                            {isActive && detail.tracking_no && (
                              <div style={{marginTop:8,fontFamily:"'Jost',sans-serif",fontSize:12,color:'#7a1f1f'}}>
                                Tracking: <strong>{detail.tracking_no}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── ORDER ITEMS ── */}
              <div style={{background:'#fff',border:'1px solid var(--border)',padding:24,marginBottom:24}}>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:3,textTransform:'uppercase',color:'#8a7968',marginBottom:16}}>Items Ordered</div>
                {(detail.items||[]).map((item,i) => (
                  <div key={i} style={{display:'flex',gap:16,alignItems:'center',paddingBottom:16,marginBottom:16,borderBottom:'1px solid #f0e8e0'}}>
                    <img src={resImg(item.photo)} alt={item.name} style={{width:64,height:80,objectFit:'cover',background:'#f7f0e6',flexShrink:0}} onError={e=>{e.target.src=PH;}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,color:'#1a0f0f',marginBottom:2}}>{item.name}</div>
                      <div style={{fontSize:11,color:'#8a7968',fontFamily:"'Jost',sans-serif"}}>Qty: {item.qty} × <span className="price-display" style={{fontSize:13,color:'#7a1f1f'}}>₹{formatINR(item.unit_price)}</span></div>
                    </div>
                    <div className="price-display price-sm" style={{flexShrink:0}}>₹{formatINR(item.unit_price * item.qty)}</div>
                  </div>
                ))}
              </div>

              {/* ── DELIVERY ADDRESS ── */}
              {detail.address_json && (
                <div style={{background:'#fff',border:'1px solid var(--border)',padding:24}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:3,textTransform:'uppercase',color:'#8a7968',marginBottom:12}}>Delivery Address</div>
                  <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,color:'#1a0f0f',fontWeight:600,marginBottom:4}}>{detail.address_json.name}</div>
                  <div style={{fontSize:13,color:'#5a3a3a',lineHeight:1.7}}>
                    {detail.address_json.address}<br/>
                    {detail.address_json.landmark && <>{detail.address_json.landmark}<br/></>}
                    {detail.address_json.city}, {detail.address_json.state} — {detail.address_json.pincode}<br/>
                    📞 {detail.address_json.phone}
                  </div>
                </div>
              )}
            </div>

            {/* ── PRICE SUMMARY ── */}
            <div>
              <div style={{background:'#fff',border:'1px solid var(--border)',padding:22,marginBottom:16}}>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:3,textTransform:'uppercase',color:'#8a7968',marginBottom:16}}>Price Details</div>
                {[
                  ['Subtotal',  `₹${formatINR(detail.subtotal)}`],
                  detail.discount > 0 && ['Coupon Discount', `-₹${formatINR(detail.discount)}`, '#2e7d32'],
                  ['Shipping',  detail.shipping > 0 ? `₹${detail.shipping}` : 'Free 🎉', detail.shipping===0?'#2e7d32':undefined],
                ].filter(Boolean).map(([k,v,c])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',fontSize:13,color:c||'#5a3a3a',marginBottom:10,fontFamily:"'Jost',sans-serif"}}>
                    <span>{k}</span><span style={c?{color:c,fontWeight:600}:{}}>{v}</span>
                  </div>
                ))}
                {detail.coupon_code && (
                  <div style={{fontSize:11,color:'#2e7d32',marginBottom:10,fontFamily:"'Jost',sans-serif"}}>🎟 Coupon: <strong>{detail.coupon_code}</strong></div>
                )}
                <div style={{display:'flex',justifyContent:'space-between',borderTop:'2px solid var(--border)',paddingTop:14,fontFamily:"'Cormorant Garamond',serif",fontSize:22,color:'#7a1f1f',fontWeight:600}}>
                  <span>Total Paid</span><span>₹{formatINR(detail.total)}</span>
                </div>
                {detail.razorpay_payment_id && (
                  <div style={{marginTop:10,fontSize:11,color:'#8a7968',fontFamily:"'Jost',sans-serif"}}>Payment ID: {detail.razorpay_payment_id}</div>
                )}
              </div>

              {/* Help */}
              <div style={{background:'#f7f0e6',padding:20,textAlign:'center',border:'1px solid var(--border)'}}>
                <div style={{fontSize:13,color:'#5a3a3a',marginBottom:12,fontFamily:"'Cormorant Garamond',serif"}}>Need help with this order?</div>
                <a href={`https://wa.me/919963686116?text=Hi!%20I%20need%20help%20with%20order%20${detail.id}`} target="_blank" rel="noopener noreferrer"
                  style={{display:'inline-flex',alignItems:'center',gap:8,background:'#25D366',color:'#fff',padding:'10px 20px',textDecoration:'none',fontFamily:"'Jost',sans-serif",fontSize:12,fontWeight:600}}>
                  💬 WhatsApp Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── ORDERS LIST ───────────────────────────────────────────────
  return (
    <div className="page">
      <div className="container" style={{padding:'48px 24px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:32,flexWrap:'wrap',gap:12}}>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:32,color:'#7a1f1f',fontStyle:'italic'}}>My Orders</h1>
          <Link to="/collections" className="btn btn-outline" style={{fontSize:11}}>Continue Shopping →</Link>
        </div>

        {orders.length === 0 ? (
          <div style={{textAlign:'center',padding:'64px 0'}}>
            <div style={{fontSize:56}}>📦</div>
            <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:'#7a1f1f',fontStyle:'italic',margin:'12px 0 8px'}}>No orders yet</h3>
            <p style={{color:'#8a7968',marginBottom:20}}>Start shopping to see your orders here</p>
            <Link to="/collections" className="btn btn-primary">Explore Collections</Link>
          </div>
        ) : (
          <div>
            {orders.map(order => {
              const meta = STATUS_META[order.status] || STATUS_META.confirmed;
              return (
                <div key={order.id} onClick={()=>navigate(`/orders/${order.id}`)}
                  style={{border:'1px solid var(--border)',padding:20,marginBottom:14,background:'#fff',cursor:'pointer',transition:'box-shadow 0.2s'}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 20px rgba(122,31,31,0.1)'}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12}}>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:'#8a7968',letterSpacing:1,textTransform:'uppercase',marginBottom:4}}>Order ID</div>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,color:'#2d1f1f',fontWeight:600}}>{order.id}</div>
                      <div style={{fontSize:12,color:'#8a7968',marginTop:4,fontFamily:"'Jost',sans-serif"}}>{new Date(order.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div className="price-display price-md" style={{marginBottom:8}}>₹{formatINR(order.total)}</div>
                      <div style={{display:'inline-flex',alignItems:'center',gap:5,padding:'4px 12px',fontSize:10,fontWeight:700,background:meta.bg,color:meta.color,letterSpacing:1,textTransform:'uppercase',fontFamily:"'Jost',sans-serif"}}>
                        {meta.icon} {meta.label}
                      </div>
                    </div>
                  </div>
                  <div style={{marginTop:12,display:'flex',justifyContent:'flex-end'}}>
                    <span style={{fontSize:12,color:'#7a1f1f',fontFamily:"'Jost',sans-serif",letterSpacing:0.5}}>View Details →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
